import React, { Fragment, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiCheckBoxGroup from "Components/MuiCheckboxGroup/MuiCheckboxGroup";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { useDispatch } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import { getAccountingDefaultChargeAgain } from "Stores/actions";

export default function AddDefaultCharge({ disabled }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [controls, setControls] = useSetState({
    title: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    price: {
      type: "price",
      value: "",
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
    },
    priceCredit: {
      type: "price",
      value: "",
      validation: {
        required: true,
        isPrice: true,
        notZero: true,
      },
    },
    options: {
      type: "checkbox",
      items: [{ value: false, key: "isActive" }],
    },
    description: {
      type: "text",
      value: "",
      validation: null,
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = () => {
    // reset variables
    setControls({ title: { value: "" } });
    setControls({ price: { value: "" } });
    setControls({ priceCredit: { value: "" } });
    setControls({ description: { value: "" } });
    controls.options.items.map((items) => (items.value = false));
    setFormValid({ title: false, price: false, priceCredit: false });
  };

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const checkboxHandler = (e, name, index) => {
    const items = controls[name].items;
    items[index] = { ...items[index], value: e.currentTarget.checked };
    setControls({ [name]: { items: items } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { title, price, priceCredit, description, options } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const data = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          Title: title.value,
          Price: +price.value,
          PriceCredit: +priceCredit.value,
          Describ: description.value,
          Active: options.items[0].value,
        };
        const response = await AccountService.postAccountingDefaultCharge(data);
        // console.log(response);
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            await dispatch(getAccountingDefaultChargeAgain());
            onExit();
          }
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } else {
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[AddDefaultCharge]", err);
      setLoading(false);
    }
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <MuiButton
        color="primary"
        title={<Translator id="addDefaultCharge" />}
        innerIcon={<Icons.PlusLg />}
        onClick={onShowDialog}
        disabled={disabled || !read("FormDefaultCredit").accessRegister}
        hidden={!read("FormDefaultCredit").accessRegister}
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="sm"
      >
        <div className="dialogHeader">
          <p>{<Translator id={"addDefaultCharge"} />}</p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls).map((key) => {
              return (
                <Grid
                  item
                  sm={key === "description" ? 12 : 6}
                  xs={12}
                  key={key}
                >
                  {(controls[key].type === "text" ||
                    controls[key].type === "price") && (
                    <Input
                      name={key}
                      setValue={setValue.bind(this)}
                      validation={controls[key].validation}
                      placeholder={t(key)}
                      label={<Translator id={key} />}
                      type={controls[key].type}
                      checkValid={checkValid}
                      value={controls[key].value}
                      endIcon={
                        controls[key].type === "price" ? (
                          <Translator id="moneyUnit" />
                        ) : (
                          ""
                        )
                      }
                    />
                  )}
                  {controls[key].type === "checkbox" && (
                    <MuiCheckBoxGroup
                      translate={true}
                      name={key}
                      value={controls[key].value}
                      items={controls[key].items}
                      onChange={checkboxHandler}
                      className="horizontalCheckbox"
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
